/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Author Nirjhor
 * Title: Center Stock Reconcile
 * Description: Center Stock Reconcile
 * Date: 13/08/2026
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import CommonApiService from '../../../services/CommonApiService';
import UrlService from '../../../services/UrlService';
import { useStockReconcile } from './ContextAPI';
import LoaderSubmit from '../../LoaderSubmit';

export default function FormInfo({ editData = null }) {
    const { setShow, addStockReconcile, editStockReconcile } = useStockReconcile();
    const axiosPrivate = useAxiosPrivate();
    // Local State
    const [drugData, setDrugData] = useState([]);
    const [itemDetails, setItemDetails] = useState([]);
    const [lossReasonData, setLossReasonData] = useState();
    const [batchNoData, setBatchNoData] = useState();
    const [poData, setPoData] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [availableQty, setAvailableQty] = useState(null);
    const [currentItemId, setCurrentItemId] = useState(null);

    const { drugInfo, getDrugInfo } = CommonApiService();

    const {
        register,
        formState: { errors },
        reset,
        handleSubmit,
        control,
        setValue,
    } = useForm({
        mode: 'onTouched',
        criteriaMode: 'firstError',
        reValidateMode: 'onBlur',
        defaultValues: useMemo(
            () => ({
                ...editData,
                date: editData ? new Date(editData.date) : new Date(),
            }),
            [editData]
        ),
    });

    // Reset default values
    useEffect(() => {
        reset({
            ...editData,
            date: editData ? new Date(editData.date) : new Date(),
        });
    }, [editData]);

    // Item Details
    const {
        register: register2,
        formState: { errors: errors2 },
        handleSubmit: handleSubmit2,
        reset: reset2,
        control: control2,
        watch: watch2,
        setValue: setValue2,
    } = useForm({
        mode: 'onTouched',
        criteriaMode: 'firstError',
        reValidateMode: 'onBlur',
    });

    // Set default values
    useEffect(() => {
        setValue('itemDetails', itemDetails);
    }, [itemDetails]);

    const dataList = useCallback(() => {
        getDrugInfo(); // DrugInfo
    }, []);

    useEffect(() => {
        dataList();
    }, [dataList, editData]);

    useEffect(() => {
        // DrugInfo
        setDrugData(
            drugInfo?.map((el) => ({
                value: el.drug_id,
                label: el.drug_name,
            }))
        );
    }, [drugInfo]);

    // Item Details: Add
    const itemDetailsAppendHandler = (data) => {
        const detailFormData = {
            ...data,
            drug_id: data.drug_id.value,
            po_no: data.po_no.value,
            batch_no: data.batch_no.value,
            loss_reason: data.loss_reason.value,
            item: { value: data.drug_id.value, label: data.drug_id.label },
            pono: { value: data.po_no.value, label: data.po_no.label },
            batchno: { value: data.batch_no.value, label: data.batch_no.label },
            lossreason: { value: data.loss_reason.value, label: data.loss_reason.label },
        };
        setItemDetails([...itemDetails, detailFormData]);
        reset2({
            drug_id: '',
            po_no: '',
            batch_no: '',
            loss_qty: '',
            loss_reason: '',
            loss_remark: '',
        });
        if (lossReasonData?.[0]) setValue2('loss_reason', lossReasonData[0]);
        setAvailableQty(null);
        setCurrentItemId(null);
        setBatchNoData([]);
        setPoData([]);
    };
    // Item Details: Remove
    const itemDetailsRemoveHandler = (drug_id) => {
        const updatedItemDetails = itemDetails && itemDetails.filter((m) => m.drug_id !== drug_id);
        setItemDetails(updatedItemDetails);
    };

    // batchNo: Fatch All Data
    const batchNo = async (id, poNumber = null) => {
        try {
            const payload = poNumber ? { item_id: id, po_no: poNumber } : { item_id: id };
            const response = await axiosPrivate.post(
                UrlService.centerBatchNoListUtilityUrl(),
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            setBatchNoData(
                response.data.batch_no_list.map((el) => ({
                    value: el.batch_no,
                    label: el.batch_no,
                }))
            );
        } catch (err) {
            if (!err?.response) {
                console.log('Server Not Responding!');
            } else if (err.response?.status === 401) {
                console.log('Unauthorized!');
            } else {
                console.log('Data Not Found!');
            }
        }
    };

    // poNo: Fatch All Data for selected item
    const poNo = async (itemId) => {
        try {
            const response = await axiosPrivate.post(
                UrlService.centerPoListUtilityUrl(),
                { item_id: itemId },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            setPoData(
                (response.data.po_list || []).map((el) => ({
                    value: el.po_no,
                    label: el.po_no,
                }))
            );
        } catch (err) {
            if (!err?.response) {
                console.log('Server Not Responding!');
            } else if (err.response?.status === 401) {
                console.log('Unauthorized!');
            } else {
                console.log('Data Not Found!');
            }
            setPoData([]);
        }
    };

    // Fetch available qty for selected item + batch
    const fetchAvailableQty = async (itemId, batchNumber) => {
        try {
            const response = await axiosPrivate.post(
                UrlService.centerBatchAvailableQtyUtilityUrl(),
                { item_id: itemId, batch_no: batchNumber },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.status === 'success') {
                setAvailableQty(response.data.available_qty);
            } else {
                setAvailableQty(null);
            }
        } catch (err) {
            if (!err?.response) {
                console.log('Server Not Responding!');
            } else if (err.response?.status === 401) {
                console.log('Unauthorized!');
            } else {
                console.log('Data Not Found!');
            }
            setAvailableQty(null);
        }
    };

    // lossReason: Only "Reconcile" type - auto create if not exists
    const lossReason = async () => {
        try {
            const response = await axiosPrivate.get(UrlService.lossReasonUtilityUrl(), {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const reconcileList = (response.data.loss_reason_list || []).filter(
                (el) => el.title?.trim()?.toLowerCase() === 'reconcile'
            );

            if (reconcileList.length > 0) {
                const reconcile = reconcileList[0];
                const option = { value: reconcile.id, label: reconcile.title };
                setLossReasonData([option]);
                setValue2('loss_reason', option);
            } else {
                // Create "Reconcile" type if not exists
                try {
                    const createResponse = await axiosPrivate.post(
                        UrlService.saveLossReasonUtilityUrl(),
                        JSON.stringify({ title: 'Reconcile' }),
                        {
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        }
                    );

                    const created = createResponse?.data?.loss_reason;
                    if (created?.id) {
                        const option = { value: created.id, label: created.title };
                        setLossReasonData([option]);
                        setValue2('loss_reason', option);
                    }
                } catch (createErr) {
                    if (!createErr?.response) {
                        console.log('Server Not Responding!');
                    } else {
                        console.log('Reconcile Type Create Failed!');
                    }
                }
            }
        } catch (err) {
            if (!err?.response) {
                console.log('Server Not Responding!');
            } else if (err.response?.status === 401) {
                console.log('Unauthorized!');
            } else {
                console.log('Data Not Found!');
            }
        }
    };

    useEffect(() => {
        lossReason();
    }, []);

    register('itemDetails', { required: 'Item details is required!' });

    // Form Submit Handelar
    const formSubmitHandler = (data) => {
        setIsSubmitted(true);

        const formData = {
            ...data,
            date: data.date.toLocaleDateString('sv-SE'),
            status: data.status === true ? 1 : 0,
        };
        if (editData) {
            editStockReconcile(formData, editData.id); // Update Data
        } else {
            addStockReconcile(formData); // Create Data
        }
        setShow(false);
        reset();
    };

    return (
        <Form>
            <fieldset style={{ background: '#f3f3f3' }} className="customFieldset p-3 pb-0 mb-3">
                <Row>
                    <Col lg={6}>
                        <Form.Group className="mb-3">
                            <Row>
                                <Col lg={3}>
                                    <Form.Label>
                                        Date<span className="required">*</span>
                                    </Form.Label>
                                </Col>
                                <Col lg={9}>
                                    <Controller
                                        control={control}
                                        name="date"
                                        render={({ field }) => (
                                            <DatePicker
                                                {...field}
                                                closeOnScroll
                                                placeholderText="Select date"
                                                dateFormat="dd-MM-yyyy"
                                                onChange={(date) => field.onChange(date)}
                                                selected={field.value}
                                                className="form-control"
                                                popperPlacement="top-end"
                                                minDate={new Date()}
                                            />
                                        )}
                                        rules={{
                                            required: 'Please select your date.',
                                            valueAsDate: true,
                                        }}
                                    />
                                    {errors.date && (
                                        <span className="invalid">{errors.date?.message}</span>
                                    )}
                                </Col>
                            </Row>
                        </Form.Group>
                    </Col>
                    <Col lg={6}>
                        <Form.Group className="mb-3">
                            <Row>
                                <Col lg={3}>
                                    <Form.Label>
                                        Reconcile Type<span className="required">*</span>
                                    </Form.Label>
                                </Col>
                                <Col lg={9}>
                                    <Controller
                                        name="loss_reason"
                                        id="loss_reason"
                                        control={control2}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                classNamePrefix={
                                                    errors2?.loss_reason && 'invalidInput'
                                                }
                                                options={lossReasonData}
                                            />
                                        )}
                                        rules={{
                                            required: 'Please select drug item.',
                                        }}
                                    />
                                    {errors2.loss_reason && (
                                        <span className="invalid">
                                            {errors2.loss_reason?.message}
                                        </span>
                                    )}
                                </Col>
                            </Row>
                        </Form.Group>
                    </Col>
                </Row>
            </fieldset>
            <Row className="mb-3">
                <Col lg={11}>
                    <Row>
                        <Col lg={6} className="pe-0">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Item Name<span className="required">*</span>
                                </Form.Label>
<Controller
                                name="drug_id"
                                id="drug_id"
                                control={control2}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        classNamePrefix={errors2?.drug_id && 'invalidInput'}
                                        options={drugData}
                                        onChange={(selected) => {
                                            field.onChange(selected);
                                            if (selected?.value) {
                                                setCurrentItemId(selected.value);
                                                setAvailableQty(null);
                                                setBatchNoData([]);
                                                setPoData([]);
                                                poNo(selected.value);
                                            } else {
                                                setCurrentItemId(null);
                                                setAvailableQty(null);
                                                setBatchNoData([]);
                                                setPoData([]);
                                            }
                                            // Reset dependent fields when item changes
                                            reset2({
                                                drug_id: selected,
                                                po_no: '',
                                                batch_no: '',
                                                loss_qty: '',
                                                loss_reason: '',
                                                loss_remark: '',
                                            });
                                            if (lossReasonData?.[0]) {
                                                setValue2('loss_reason', lossReasonData[0]);
                                            }
                                        }}
                                    />
                                )}
                                rules={{
                                    required: 'Please select drug item.',
                                }}
                            />
                                {errors2.drug_id && (
                                    <span className="invalid">{errors2.drug_id?.message}</span>
                                )}
                            </Form.Group>
                        </Col>
                        <Col lg={3} className="pe-0">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    PO No<span className="required">*</span>
                                </Form.Label>
                                <Controller
                                    name="po_no"
                                    id="po_no"
                                    control={control2}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            classNamePrefix={errors2?.po_no && 'invalidInput'}
                                            options={poData}
                                            onChange={(selected) => {
                                                field.onChange(selected);
                                                if (selected?.value && currentItemId) {
                                                    setAvailableQty(null);
                                                    batchNo(currentItemId, selected.value);
                                                } else {
                                                    setBatchNoData([]);
                                                    setAvailableQty(null);
                                                }
                                            }}
                                        />
                                    )}
                                    rules={{
                                        required: 'Please select PO number.',
                                    }}
                                />
                                {errors2.po_no && (
                                    <span className="invalid">{errors2.po_no?.message}</span>
                                )}
                            </Form.Group>
                        </Col>
                        <Col lg={3} className="pe-0">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Batch No<span className="required">*</span>
                                </Form.Label>
<Controller
                                name="batch_no"
                                id="batch_no"
                                control={control2}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        classNamePrefix={errors2?.batch_no && 'invalidInput'}
                                        options={batchNoData}
                                        onChange={(selected) => {
                                            field.onChange(selected);
                                            if (selected?.value && currentItemId) {
                                                fetchAvailableQty(currentItemId, selected.value);
                                            } else {
                                                setAvailableQty(null);
                                            }
                                        }}
                                    />
                                )}
                                rules={{
                                    required: 'Please select drug item.',
                                }}
                            />
                                {errors2.batch_no && (
                                    <span className="invalid">{errors2.batch_no?.message}</span>
                                )}
                            </Form.Group>
                        </Col>
                        <Col lg={2} className="pe-0">
                            <Form.Group>
                                <Form.Label>
                                    Reconcile Qty<span className="required">*</span>
                                </Form.Label>
                                <Form.Control
                                    name="loss_qty"
                                    id="loss_qty"
                                    type="number"
                                    placeholder=""
                                    autoComplete="off"
                                    {...register2('loss_qty', {
                                        required: 'Field is required.',
                                    })}
                                    className={errors2?.loss_qty && 'invalidInput'}
                                />
                                {errors2.loss_qty && (
                                    <span className="invalid">{errors2.loss_qty?.message}</span>
                                )}
                                {availableQty !== null && (
                                    <small
                                        className={`form-text d-block mt-1 fw-bold ${
                                            watch2('loss_qty') && Number(watch2('loss_qty')) > availableQty
                                                ? 'text-danger'
                                                : 'text-success'
                                        }`}
                                    >
                                        Available Qty: {availableQty}
                                    </small>
                                )}
                            </Form.Group>
                        </Col>
                        <Col lg={10} className="pe-0">
                            <Form.Group>
                                <Form.Label>Remark</Form.Label>
                                <Form.Control
                                    name="loss_remark"
                                    id="loss_remark"
                                    type="text"
                                    placeholder=""
                                    autoComplete="off"
                                    {...register2('loss_remark', {
                                        // required: 'Please enter your loss_remark.',
                                    })}
                                    className={errors2?.loss_remark && 'invalidInput'}
                                />
                                {errors2.loss_remark && (
                                    <span className="invalid">{errors2.loss_remark?.message}</span>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                </Col>
                <Col lg={1} className="pt-3">
                    <Button
                        className="btnSteelBlue h-100"
                        variant="primary"
                        onClick={handleSubmit2(itemDetailsAppendHandler)}
                        disabled={
                            !watch2('loss_qty') ||
                            (availableQty !== null && Number(watch2('loss_qty')) > availableQty)
                        }
                    >
                        Add
                    </Button>
                </Col>
            </Row>
            <Row>
                {errors.itemDetails && (
                    <span className="invalid">{errors.itemDetails?.message}</span>
                )}
                <Col lg={12}>
                    <Table className="borderDashed" responsive="sm">
                        <tbody>
                            {itemDetails.map((item) => (
                                <tr key={Math.random()}>
                                    <td>{item.item?.label}</td>
                                    <td>{item.pono?.label}</td>
                                    <td>{item.batchno?.label}</td>
                                    <td>{item.lossreason?.label}</td>
                                    <td>{item.loss_qty}</td>
                                    <td>{item.loss_remark}</td>
                                    <td className="text-end">
                                        <Button
                                            className="btn btn-light actionBtn"
                                            variant="primary"
                                            onClick={() => itemDetailsRemoveHandler(item.drug_id)}
                                        >
                                            <i className="fas fa-trash-alt" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Row>
                <Form.Group className="mb-3">
                    <Row className="text-start">
                        <Col lg={12}>
                            <Button
                                onClick={handleSubmit(formSubmitHandler)}
                                className="btnSuccess"
                                type="button"
                                variant="success"
                                disabled={isSubmitted}
                            >
                                {editData ? 'Update' : 'Submit'}
                            </Button>
                            {isSubmitted && <LoaderSubmit />}
                        </Col>
                    </Row>
                </Form.Group>
            </Row>
        </Form>
    );
}
